import _ from 'lodash';
import { Node } from 'slate';

import { CART_STATUS, EVENT_REQUEST_TYPE } from '@/config/common';
import copyToClipBoard from '@/helpers/copyToClipBoard';
import { convertToShortMonthDateFormat, formatTimeToAmPm } from '@/lib/time';
import { parseNumberedLabel } from '@/lib/utils';
import { getUserEventIdFromSelectedCartCookie } from '@/services/cart.service';
import {
  COOKIE_MAP,
  getCookie,
  removeCookie,
  setCookie
} from '@/services/cookie.service';
import * as HttpService from '@/services/http.service';
import {
  GET_CARTS_BY_EVENT_ID,
  GET_USER_EVENT_DETAILS_BY_ID
} from '@/services/url.service';

const getCartsByEventId = ({ userCartId, userEventId }) =>
  HttpService.getWithAuth(GET_CARTS_BY_EVENT_ID({ userCartId, userEventId }));

export const getUserEventDetailsById = ({ userId, userEventId }) =>
  HttpService.getWithAuth(
    GET_USER_EVENT_DETAILS_BY_ID({ userId, userEventId })
  );

export const getSelectedUserEvent = () =>
  getCookie({ name: COOKIE_MAP.SELECTED_USER_EVENT });

const DUMMY_EVENT_DETAILS = {
  checkoutEventId: null,
  contactMethodId: null,
  eventContactEmail: 'haflacustomer',
  eventContactName: 'ZD_AUTO_CUSTOMER',
  eventDate: '31st Dec 2099',
  eventDurationInDays: null,
  eventSiteTypeId: null,
  eventStartTime: '24:00',
  eventTitle: null,
  eventVerticals: [],
  expectedGuestCount: null,
  formattedAddress: 'Nook Coworking - Dubai - United Arab Emirates',
  hostCreditEntityId: null,
  opportunityValue: null,
  requesterType: null,
  theme: null,
  triageSummary: '',
  zendeskTicketId: null
};

const MANDATORY_FIELDS_FOR_EPIC_GENERATED = {
  EVENT_DATE: 'eventDate',
  EVENT_TYPE: 'checkoutEventId',
  EVENT_VERTICALS: 'eventVerticals',
  EXPECTED_GUEST_COUNT: 'expectedGuestCount',
  REQUESTER_NAME: 'eventContactName',
  REQUESTER_TYPE: 'requesterType',
  TRIAGE_SUMMARY: 'triageSummary',
  ZENDESK_TICKET_ID: 'zendeskTicketId'
};

const SLATE_SERIALIZER_DELIMITER = '\n';

export const setSelectedUserEvent = ({
  data: userEventId,
  resetPreviousCartSelectionForSameEvent = false
}) => {
  const currentSelectedCart = getCookie({ name: COOKIE_MAP.SELECTED_CART });
  const isCurrentSelectedCartDefaultCart = currentSelectedCart?.isDefault;
  const currentSelectedCartEventId =
    getUserEventIdFromSelectedCartCookie(currentSelectedCart);

  const noUserEventChange = currentSelectedCartEventId === userEventId;

  const eventCookie = setCookie({
    data: userEventId,
    name: COOKIE_MAP.SELECTED_USER_EVENT
  });

  if (noUserEventChange && resetPreviousCartSelectionForSameEvent) {
    return {
      cartCookie: removeCookie({ name: COOKIE_MAP.SELECTED_CART }),
      eventCookie
    };
  }

  if (noUserEventChange || isCurrentSelectedCartDefaultCart) {
    return {
      cartCookie: currentSelectedCart,
      eventCookie
    };
  }

  return {
    cartCookie: removeCookie({ name: COOKIE_MAP.SELECTED_CART }),
    eventCookie
  };
};

const fetchCartListByEventId = async ({
  setCartCardList,
  userCartId,
  userEventId
}) => {
  const { status: getCartsByEventIdStatus, entity: cartList } =
    await getCartsByEventId({
      userCartId,
      userEventId
    });
  if (getCartsByEventIdStatus) {
    setCartCardList(cartList);
  }
};

export const fetchUserEventCartDetails = async ({
  getUserEventCartList,
  setCartCardList,
  setLoading,
  setUserEvent,
  userCartId,
  userEventId,
  userId
}) => {
  setLoading(true);
  const { status, entity: userEvent } = await getUserEventDetailsById({
    userEventId,
    userId
  });
  setLoading(false);
  if (status || _.isEmpty(userEvent)) {
    setSelectedUserEvent({
      data: userEventId
    });
    await getUserEventCartList();
    setUserEvent(userEvent);
    await fetchCartListByEventId({ userCartId, userEventId, setCartCardList });
  }
};

export const filterActiveCart = ({ status, isArchived }) =>
  [CART_STATUS.DRAFT.value, CART_STATUS.COLLABORATE.value].includes(status) &&
  !isArchived;

export const filterArchivedCart = ({ isArchived }) => isArchived;

export const filterOrderedCart = ({ status }) =>
  [CART_STATUS.ORDER.value].includes(status);

export const getLabelForEventRequestIsEndToEnd = (eventRequestIsEndToEnd) =>
  eventRequestIsEndToEnd
    ? EVENT_REQUEST_TYPE.END_TO_END
    : EVENT_REQUEST_TYPE.PRODUCT_AND_SERVICE_ONLY;

export const getSidebarBodyData = ({
  eventAddress,
  eventContactName,
  eventDate,
  eventStartTime,
  eventVerticals,
  expectedGuestCount,
  isMobile,
  zendeskTicketId
}) => {
  const timeInAmPm = formatTimeToAmPm(eventStartTime);
  const eventDateTime = eventDate
    ? `${convertToShortMonthDateFormat(eventDate)}, ${timeInAmPm}`
    : 'NA';

  return [
    {
      className: 'justify-start gap-4 my-1',
      icon: 'zendesk-icon-v1.svg',
      iconHeight: 24,
      iconWidth: 24,
      id: 'zendesk-ticket-number',
      label: parseNumberedLabel(zendeskTicketId),
      labelStyle: `font-medium cursor-pointer self-center text-dim-gray cursor-pointer hover:!text-brand ${
        zendeskTicketId ? '' : 'text-error-base'
      }`
    },
    {
      className: 'justify-start gap-4 my-1',
      icon: 'host-brand-icon-v1.svg',
      iconHeight: 24,
      iconWidth: 24,
      id: 'host-name',
      label: eventContactName || 'NA',
      labelStyle: `font-medium cursor-pointer self-center text-dim-gray cursor-pointer hover:!text-brand ${
        isMobile ? 'truncate-text-one-line' : ''
      }`
    },
    {
      className: 'justify-start gap-4 my-1',
      icon: 'event-profile-icon.svg',
      iconHeight: 24,
      iconWidth: 24,
      id: 'event-date-time',
      label: eventDateTime,
      labelStyle: `font-medium self-center text-dim-gray`
    },
    {
      className: 'justify-start gap-4 my-1',
      icon: 'location-icon.svg',
      iconHeight: 24,
      iconWidth: 24,
      id: 'event-location',
      label: eventAddress?.formattedAddress || 'NA',
      labelStyle: `font-medium self-center text-dim-gray ${
        isMobile ? 'truncate-text-one-line' : ''
      }`
    },
    {
      className: 'justify-start gap-4 my-1',
      icon: 'core-verticals-icon.svg',
      iconHeight: 24,
      iconWidth: 24,
      id: 'hafla-request-verticals',
      label:
        eventVerticals.length > 0
          ? eventVerticals.map(({ name }) => name).join(', ')
          : 'NA',
      labelStyle: 'font-medium self-center text-dim-gray'
    },
    {
      className: 'justify-start gap-4 my-1',
      icon: 'guest-icon.svg',
      iconHeight: 24,
      iconWidth: 24,
      id: 'expected-guests',
      label: expectedGuestCount ?? 'NA',
      labelStyle: 'font-medium self-center text-dim-gray'
    }
  ];
};

const checkIsEmptyOrDummyEventDetail = ({ key, value }) => {
  const dummyValue = _.get(DUMMY_EVENT_DETAILS, key, 'key-not-found');
  if (_.isNil(value)) {
    return true;
  }

  if (dummyValue === 'key-not-found') {
    return false;
  }

  if (_.isNumber(value)) {
    return dummyValue === value;
  }

  if (_.isString(value) && _.isString(dummyValue)) {
    if (value?.trim() === '') {
      return true;
    }

    if (dummyValue?.trim() !== '') {
      return value.includes(dummyValue);
    }

    return false;
  }

  if (!_.isBoolean(value) && _.isEmpty(value)) {
    return true;
  }

  return false;
};

const parseUserEventInfoForEPIC = ({ key, placeholderValue, value }) =>
  checkIsEmptyOrDummyEventDetail({ key, value })
    ? placeholderValue
    : `${value}`;

export const getEPICExplanationStringInputs = ({
  epicParameters: {
    city,
    eventDate,
    eventType,
    guests,
    hostName,
    hostType,
    verticals
  }
}) => [
  {
    prefix: '',
    rank: 1, // TODO: Sidd to revisit, once feature is stable remove key
    value: parseUserEventInfoForEPIC({
      key: 'eventContactName',
      placeholderValue: '{Host Name}',
      value: hostName
    })
  },
  {
    key: 'requesterType',
    prefix: ', a(n) ',
    rank: 2,
    value: parseUserEventInfoForEPIC({
      key: 'requesterType',
      placeholderValue: '{Host Type}',
      value: hostType
    })
  },
  {
    prefix: ', is planning a ',
    rank: 3,
    value: parseUserEventInfoForEPIC({
      key: 'eventType',
      placeholderValue: '{Event Type}',
      value: eventType
    })
  },
  {
    prefix: ' on ',
    rank: 4,
    value: parseUserEventInfoForEPIC({
      key: 'eventDate',
      placeholderValue: '{Event Date}',
      value: eventDate ? convertToShortMonthDateFormat(eventDate) : null
    })
  },
  {
    prefix: ' for ',
    rank: 5,
    value: parseUserEventInfoForEPIC({
      key: 'expectedGuestCount',
      placeholderValue: '{No. of guests}',
      value: guests
    })
  },
  {
    prefix: ' guests in ',
    rank: 6,
    value: parseUserEventInfoForEPIC({
      key: 'formattedAddress',
      placeholderValue: '{City}',
      value: city
    })
  },
  {
    rank: 7,
    prefix: ' and is looking for ',
    value: parseUserEventInfoForEPIC({
      key: 'eventVerticals',
      placeholderValue: '{Hafla Request Vertical(s)}',
      value:
        Array.isArray(verticals) && verticals.length > 0
          ? verticals.map(({ name }) => name).join(', ')
          : verticals
    })
  }
];

export const generateEPICExplanationAndCopyToClipboard = async ({
  epicParameters,
  setShowToast = () => {}
}) => {
  const isClipboardAvailable = Boolean(navigator.clipboard);
  if (isClipboardAvailable) {
    const epicExplanation = getEPICExplanationStringInputs({
      epicParameters
    })
      .map(({ prefix, value }) => `${prefix}${value}`)
      .join('');

    await copyToClipBoard(epicExplanation);
  }
  setShowToast({
    message: isClipboardAvailable
      ? 'Successfully copied to clipboard'
      : 'Failed to copy to clipboard',
    show: true,
    successToast: isClipboardAvailable
  });
};

const getSerializedRichText = (value) => {
  try {
    const nodes = Array.isArray(value) ? value : value?.inclusions;

    if (_.isEmpty(nodes)) {
      return false;
    }

    if (Array.isArray(nodes)) {
      return nodes
        .map((node) => Node.string(node))
        .join(SLATE_SERIALIZER_DELIMITER);
    }

    return Node.string(nodes);
  } catch {
    return false;
  }
};

const getKeyValuePairsForEPICGeneratedStage = ({ userEvent }) => {
  const {
    user,
    userEventDetails: [eventDetail],
    zendeskTicketId
  } = userEvent;
  const {
    checkoutEventId,
    eventContactName,
    eventDate,
    eventVerticals,
    expectedGuestCount,
    triageSummary
  } = eventDetail;
  const { hostSegment } = user || {};
  const requesterType = hostSegment?.name || null;
  return [
    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.EVENT_DATE,
      label: 'Event date',
      sortOrder: 5,
      value: eventDate ? convertToShortMonthDateFormat(eventDate) : null
    },
    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.EVENT_TYPE,
      label: 'Event type',
      sortOrder: 4,
      value: checkoutEventId
    },
    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.EVENT_VERTICALS,
      label: 'Hafla request vertical(s)',
      sortOrder: 6,
      value: eventVerticals
    },
    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.EXPECTED_GUEST_COUNT,
      label: 'Expected # guests',
      sortOrder: 7,
      value: expectedGuestCount
    },
    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.REQUESTER_TYPE,
      label: 'Host type',
      sortOrder: 2,
      value: requesterType
    },
    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.REQUESTER_NAME,
      label: 'Host name',
      sortOrder: 3,
      value: eventContactName
    },

    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.TRIAGE_SUMMARY,
      label: 'Summary',
      sortOrder: 8,
      value: getSerializedRichText(triageSummary) || null
    },
    {
      key: MANDATORY_FIELDS_FOR_EPIC_GENERATED.ZENDESK_TICKET_ID,
      label: 'Zendesk ticket',
      sortOrder: 1,
      value: zendeskTicketId
    }
  ];
};

const getIncompleteEPICGeneratedFields = ({ userEvent }) => {
  const requiredEventDetailKeyValueList = getKeyValuePairsForEPICGeneratedStage(
    {
      userEvent
    }
  );

  return requiredEventDetailKeyValueList.filter(checkIsEmptyOrDummyEventDetail);
};

export const getEPICGenerationCompletionPercentage = (userEvent) => {
  const countOfMandatoryFieldsForEPICGenerated = Object.keys(
    MANDATORY_FIELDS_FOR_EPIC_GENERATED
  ).length;

  const incompleteFieldsCount = getIncompleteEPICGeneratedFields({
    userEvent
  }).length;

  return parseFloat(
    ((countOfMandatoryFieldsForEPICGenerated - incompleteFieldsCount) * 100) /
      countOfMandatoryFieldsForEPICGenerated
  ).toFixed(2);
};
